import type { FormData } from "@amzn/aquila-service-type-script-client";
import { InsightType } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useStore } from "zustand";

import {
    BlinkWebsiteHost,
    PublisherWebsiteHost,
} from "../../../../config/auth.config";
import { authStore } from "../../../../store/auth";
import { FeatureFlagsStore } from "../../../../store/featureFlags";
import { discoveryFilterStore } from "../../../../store/filters";
import { navigationStore } from "../../../../store/navigation";
import { reportIssueStore } from "../../../../store/reportIssue";
import { salesforceStore } from "../../../../store/salesforce";
import { unifiedFilterStore } from "../../../../store/unifiedFilters";
import { AquilaReportIssueTriggerType } from "../../../../types/aquila";
import { TopNavBarTabs } from "../../../../types/navigation";
import { getAdvertiserId } from "../../../../utils/permission.utils";
import useEncodedFilterStateUrl from "../../../misc/useEncodedFilterStateUrl";
import useEncodedUnifiedFilterStateUrl from "../../../misc/useEncodedUnifiedFilterStateUrl";

export const useReportIssueContextDataSync = (): {
    getReportIssueContextData: (
        trigger: AquilaReportIssueTriggerType,
    ) => FormData[];
} => {
    const [useUnifiedSearch] = useStore(FeatureFlagsStore, (state) => [
        state.unifiedSearch,
    ]);
    const [reportIssueContextData] = useStore(reportIssueStore, (state) => [
        state.contextData,
    ]);
    const [username, settings] = useStore(authStore, (state) => [
        state.username,
        state.settings,
    ]);
    const { domainId: domainIdPathParam = "" } = useParams();
    const [sfName] = useStore(salesforceStore, (state) => [
        state.sfContext.name,
    ]);
    const [activeTab] = useStore(navigationStore, (state) => [state.activeTab]);
    const [updateReportIssueContextData] = useStore(
        reportIssueStore,
        (state) => [state.updateReportIssueContextData],
    );
    const [, getEncodedFilterStateUrl] = useEncodedFilterStateUrl();
    const encodedFilterStateUrl = getEncodedFilterStateUrl();
    const [, getEncodedUnifiedFilterStateUrl] =
        useEncodedUnifiedFilterStateUrl();
    const encodedUnifiedFilterStateUrl = getEncodedUnifiedFilterStateUrl();
    const shareUrl = useUnifiedSearch
        ? encodedUnifiedFilterStateUrl
        : encodedFilterStateUrl;
    const [contentType, contentSubType, scopeToLabel] = useStore(
        discoveryFilterStore,
        (state) => [
            state.contentType,
            state.getContentSubType(),
            state.scopeToLabel,
        ],
    );
    const [unifiedFilters] = useStore(unifiedFilterStore, (state) => [
        state.filters,
    ]);
    const contentSubTypeLabel = scopeToLabel[contentSubType?.scope ?? ""] ?? "";

    useEffect(() => {
        const sfId = getAdvertiserId() ?? "";
        let context: { [key: string]: any } = {
            sfId,
            sfName,
            username,
            activeTab,
        };
        if (activeTab === TopNavBarTabs.ALL_INSIGHTS) {
            context = {
                ...context,
                shareUrl,
                contentType,
                contentSubType,
                contentSubTypeLabel,
                domainIdPathParam,
            };
        }
        updateReportIssueContextData(context);
    }, [
        activeTab,
        sfName,
        contentType,
        contentSubType,
        contentSubTypeLabel,
        domainIdPathParam,
        shareUrl,
        updateReportIssueContextData,
    ]);

    const getReportIssueContextData = useCallback(
        (trigger: AquilaReportIssueTriggerType) => {
            const commonData = [
                {
                    label: "Logged-in User",
                    value: username,
                    field: "loggedInUser",
                },
                {
                    label: "Tab Name",
                    value: (() => {
                        const urlParams = new URLSearchParams(
                            window.location.search,
                        );
                        const initialTab = urlParams.get("initialTab");
                        if (initialTab === "generated")
                            return TopNavBarTabs.YOUR_INSIGHT_DECKS;
                        if (initialTab === "exploreWithAI")
                            return TopNavBarTabs.EXPLORE_WITH_AI;
                        if (initialTab === "insights")
                            return TopNavBarTabs.ALL_INSIGHTS;
                        return activeTab;
                    })(),
                    field: "activeTab",
                },
                {
                    label: "Salesforce Id",
                    value: getAdvertiserId() ?? "",
                    field: "sfId",
                },
                {
                    label: "Salesforce Name",
                    value: sfName ?? "",
                    field: "sfName",
                },
            ];
            const applicationUrl = [
                {
                    label: "Application Url",
                    value: shareUrl,
                    field: "applicationUrl",
                },
            ];
            const additionalData: FormData[] = [];

            if (
                [
                    AquilaReportIssueTriggerType.ALL_INSIGHTS_SIDEPANEL,
                    AquilaReportIssueTriggerType.YOUR_INSIGHT_DECKS_SIDEPANEL,
                    AquilaReportIssueTriggerType.ALL_INSIGHTS_CREATE_ON_DEMAND,
                ].includes(trigger)
            ) {
                if (reportIssueContextData.title) {
                    additionalData.push({
                        label: "Title",
                        value: reportIssueContextData.title,
                        field: "Title",
                    });
                }
                if (reportIssueContextData.identifier) {
                    additionalData.push({
                        label: "Identifier",
                        value: reportIssueContextData.identifier,
                        field: "identifier",
                    });
                }
            }

            if (
                trigger === AquilaReportIssueTriggerType.ALL_INSIGHTS_TAB ||
                trigger === AquilaReportIssueTriggerType.ALL_INSIGHTS_SIDEPANEL
            ) {
                if (
                    trigger ===
                    AquilaReportIssueTriggerType.ALL_INSIGHTS_SIDEPANEL
                ) {
                    if (
                        reportIssueContextData.insightFamilyName &&
                        reportIssueContextData.insightType ===
                            InsightType.INSIGHT
                    ) {
                        additionalData.push({
                            label: "Insight Family Name",
                            value: reportIssueContextData.insightFamilyName,
                            field: "insightFamilyName",
                        });
                    }
                }
                return [
                    ...commonData,
                    ...additionalData,
                    ...(useUnifiedSearch
                        ? [
                              // TODO: causing payload to exceed 60kb length
                              //   {
                              //       label: "Unified Filters",
                              //       value:
                              //           Object.keys(unifiedFilters).length > 0
                              //               ? JSON.stringify(unifiedFilters)
                              //               : "None",
                              //       field: "unifiedFilters",
                              //   },
                          ]
                        : [
                              {
                                  label: "Content Type",
                                  value: contentType,
                                  field: "contentType",
                              },
                              {
                                  label: "Content SubType",
                                  value: contentSubTypeLabel,
                                  field: "contentSubType",
                              },
                          ]),
                    ...applicationUrl,
                ];
            } else if (
                trigger ===
                AquilaReportIssueTriggerType.ALL_INSIGHTS_CREATE_ON_DEMAND
            ) {
                const domainUrl = `https://${PublisherWebsiteHost[settings.stage]}/domains/${domainIdPathParam}`;
                return [
                    ...commonData,
                    {
                        label: "Domain Identifier",
                        value: domainUrl,
                        field: "domainIdentifier",
                    },
                    {
                        label: "Application Url",
                        value: window.location.href,
                        field: "applicationUrl",
                    },
                ];
            } else if (
                trigger ===
                    AquilaReportIssueTriggerType.YOUR_INSIGHT_DECKS_TAB ||
                trigger ===
                    AquilaReportIssueTriggerType.YOUR_INSIGHT_DECKS_SIDEPANEL
            ) {
                const debugSpoofUrl = `https://${BlinkWebsiteHost[settings.stage]}?sfId=${getAdvertiserId() ?? ""}&supportMode=true&spoofAsUser=${username}`;
                const domainUrl = `https://${PublisherWebsiteHost[settings.stage]}/domains/${reportIssueContextData.domainIdentifier}`;
                const templateUrl = `${domainUrl}/templates/${reportIssueContextData.templateIdentifier}`;
                if (
                    trigger ===
                    AquilaReportIssueTriggerType.YOUR_INSIGHT_DECKS_SIDEPANEL
                ) {
                    if (reportIssueContextData.templateIdentifier) {
                        additionalData.push({
                            label: "Template Identifier",
                            value: templateUrl,
                            field: "templateIdentifier",
                        });
                    }
                    if (reportIssueContextData.domainIdentifier) {
                        additionalData.push({
                            label: "Domain Identifier",
                            value: domainUrl,
                            field: "domainIdentifier",
                        });
                    }
                }
                return [
                    ...commonData,
                    ...additionalData,
                    {
                        label: "Debug Spoof Url",
                        value: debugSpoofUrl,
                        field: "debugSpoofUrl",
                    },
                ];
            } else {
                return commonData;
            }
        },
        [
            username,
            activeTab,
            sfName,
            contentType,
            contentSubTypeLabel,
            domainIdPathParam,
            shareUrl,
            settings,
            reportIssueContextData,
        ],
    );

    return {
        getReportIssueContextData,
    };
};
